import { Component, input, AfterViewInit, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import type { ComposeOption } from 'echarts/core';
import type { LineSeriesOption } from 'echarts/charts';
import type {
  TooltipComponentOption,
  GridComponentOption,
  LegendComponentOption
} from 'echarts/components';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

type EChartsOption = ComposeOption<
  LineSeriesOption |
  TooltipComponentOption |
  GridComponentOption |
  LegendComponentOption
>;

interface IDataLine {
  h: string;  // e.g., "01:00"
  v: number;
}

@Component({
  selector: 'app-insights-ff-lcr-hourly-chart',
  standalone: false,
  templateUrl: './insights-ff-lcr-hourly-chart.component.html',
  styleUrl: './insights-ff-lcr-hourly-chart.component.css',
})
export class InsightsFfLcrHourlyChartComponent implements AfterViewInit, OnDestroy {
  @ViewChild('chart') chartElement!: ElementRef<HTMLDivElement>;

  data = input.required<{ weekday: IDataLine[]; saturday: IDataLine[]; sunday: IDataLine[] }>();

  private chartInstance!: echarts.ECharts;

  ngAfterViewInit(): void {
    const chartDom = this.chartElement.nativeElement;
    this.chartInstance = echarts.init(chartDom);

    const inputData = this.data();

    const parseHour = (h: string): number => parseInt(h.split(':')[0], 10);

    // Sort each dataset by hour to ensure correct line order
    const sortByHour = (array: IDataLine[]) => array.sort((a, b) => parseHour(a.h) - parseHour(b.h));

    const weekday = sortByHour(inputData.weekday);
    const saturday = sortByHour(inputData.saturday);
    const sunday = sortByHour(inputData.sunday);

    const weekdayData = weekday.map(d => [parseHour(d.h), d.v]);
    const saturdayData = saturday.map(d => [parseHour(d.h), d.v]);
    const sundayData = sunday.map(d => [parseHour(d.h), d.v]);

    const option: EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const hourNum = params[0].value[0];
          const hourStr = hourNum.toString().padStart(2, '0') + ':00';
          let result = `Hour ${hourStr}<br/>`;
          params.forEach((p: any) => {
            result += `${p.seriesName}: ${p.value[1]}<br/>`;
          });
          return result;
        }
      },
      legend: {
        data: ['Weekday', 'Saturday', 'Sunday']
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: '',
        min: 0,
        max: 23,
        interval: 1,
        axisLabel: {
          formatter: (value: number) => `${value.toString().padStart(2, '0')}`
        }
      },
      yAxis: {
        type: 'value',
        name: 'Footfall'
      },
      series: [
        {
          name: 'Weekday',
          type: 'line',
          data: weekdayData,
          smooth: true,
          showSymbol: false,
          connectNulls: true
        },
        {
          name: 'Saturday',
          type: 'line',
          data: saturdayData,
          smooth: true,
          showSymbol: false,
          connectNulls: true
        },
        {
          name: 'Sunday',
          type: 'line',
          data: sundayData,
          smooth: true,
          showSymbol: false,
          connectNulls: true
        }
      ]
    };

    this.chartInstance.setOption(option);
  }

  ngOnDestroy(): void {
    this.chartInstance?.dispose();
  }
}
